// ts-rest contract for Comply > Call Intelligence.
// Read-only composition of the ATLAS post-call artefact rows
// (CallExtractedFact / CallActionItem / CallKbNote) + DeskCall + Client.
// Per ADR-0018 the rows the server knows the shape of are real `z.object`
// schemas — the page projects only the fields it renders.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { clientStubSchema } from '../schemas/clients';

const c = initContract();

// Reviewable lifecycle on the post-call artefact rows (ADR-0002). `pending` is
// the default; the page groups by it.
const reviewStatusSchema = z
    .enum(['pending', 'approved', 'rejected', 'revision_requested'])
    .nullable();

// The CallExtractedFact Object Type row, projected to the fields the page
// renders (camelCase wire keys mirror the ontology properties).
export const callExtractedFactDtoSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable(),
    category: z.string(),
    text: z.string(),
    sourceQuote: z.string(),
    confidence: z.enum(['high', 'medium', 'low']),
    complianceRelevant: z.boolean(),
    reviewStatus: reviewStatusSchema,
});
export type CallExtractedFactDto = z.infer<typeof callExtractedFactDtoSchema>;

// The CallActionItem Object Type row, projected to the fields the page renders.
export const callActionItemDtoSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable(),
    title: z.string(),
    owner: z.string(),
    dueDescription: z.string(),
    note: z.string().nullable(),
    reviewStatus: reviewStatusSchema,
});
export type CallActionItemDto = z.infer<typeof callActionItemDtoSchema>;

// The CallKbNote Object Type row, projected to the fields the page renders.
export const callKbNoteDtoSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable(),
    title: z.string(),
    body: z.string(),
    tags: z.array(z.string()),
    reviewStatus: reviewStatusSchema,
});
export type CallKbNoteDto = z.infer<typeof callKbNoteDtoSchema>;

// The DeskCall Object Type row, projected to the fields the page joins on.
export const callIntelligenceDeskCallDtoSchema = z.object({
    id: z.string(),
    clientId: z.string().nullable(),
    clientName: z.string(),
    callType: z.string(),
    subject: z.string(),
    date: z.string(),
    aiSummary: z.string(),
});
export type CallIntelligenceDeskCallDto = z.infer<typeof callIntelligenceDeskCallDtoSchema>;

// The Client id->name lookup the page uses to resolve a fact's clientId — the
// shared `{ id, name }` client stub (re-exported under the page-local name).
export const callIntelligenceClientDtoSchema = clientStubSchema;
export type CallIntelligenceClientDto = z.infer<typeof callIntelligenceClientDtoSchema>;

export const callIntelligenceResponseSchema = z.object({
    facts: z.array(callExtractedFactDtoSchema),
    actions: z.array(callActionItemDtoSchema),
    notes: z.array(callKbNoteDtoSchema),
    deskCalls: z.array(callIntelligenceDeskCallDtoSchema),
    clients: z.array(callIntelligenceClientDtoSchema),
});
export type CallIntelligenceResponse = z.infer<typeof callIntelligenceResponseSchema>;

const errorBody = z.object({ error: z.string() });

export const complyCallIntelligenceContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/call-intelligence',
            responses: { 200: callIntelligenceResponseSchema, 403: errorBody },
            summary: 'Composed Comply > Call Intelligence payload',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
